import type { CanonicalLeadPayload } from "@/lib/crm/intake";
import type { FitBand, FitAudience } from "@/lib/fit-band/engine";
import type { RoiInput, RoiProjection } from "@/lib/roi-calc/engine";
import {
  CURRENT_VERSION,
  DEFAULT_PROJECTION_MONTHS,
  MIN_PROJECTION_MONTHS,
  MAX_PROJECTION_MONTHS,
  DEFAULT_CALCULATION_RECEIPT,
  DEFAULT_PACKAGE_RECOMMENDATION,
  DEFAULT_SEASONAL_DEMAND,
  type FunnelTemplateId,
  type FunnelStageConfig,
  type ProjectionInputs,
  type FunnelWizardState,
  type TrafficChannel,
  type Product,
  type ScenarioSnapshot,
  type RegionalSettings,
  type FunnelGraph,
  type FunnelExpense,
  type FunnelExpenseCategory,
} from "@/lib/funnel-planner/schema";
import { getStageTypeMeta, type OutputMetric } from "@/lib/funnel-planner/stageTypes";
import { createRegionalSettings } from "@/lib/funnel-planner/regional";
import {
  computeGraphEntryVisitors,
  computeGraphStageVolumeRows,
  graphToStages,
} from "@/lib/funnel-planner/graph-utils";

import { agencyWhiteLabelTemplate }       from "./templates/agency-white-label";
import { franchiseMultiLocationTemplate }  from "./templates/franchise-multi-location";
import { aiSearchGeoTemplate }             from "./templates/ai-search-geo";
import { adultComplianceTemplate }         from "./templates/adult-compliance";
import { b2bEnterpriseTemplate }           from "./templates/b2b-enterprise";
import { ecommerceDtcTemplate }            from "./templates/ecommerce-dtc";
import { highTicketCoachingTemplate }      from "./templates/high-ticket-coaching";
import { saasPlgTemplate }                 from "./templates/saas-plg";

export type FunnelServiceLine = CanonicalLeadPayload["service_line"];

export type FunnelTemplate = {
  id: FunnelTemplateId;
  serviceLine: FunnelServiceLine;
  recommendedBands: readonly FitBand[];
  defaultStages: ReadonlyArray<{
    id: string;
    conversionRate: number;
    isEditable: boolean;
    stageType?: string;
  }>;
  defaultTrafficChannels: ReadonlyArray<TrafficChannel>;
  pdfAccentColor: string;
};

export const FUNNEL_TEMPLATES: Record<FunnelTemplateId, FunnelTemplate> = {
  "agency-white-label":       agencyWhiteLabelTemplate,
  "franchise-multi-location": franchiseMultiLocationTemplate,
  "ai-search-geo":            aiSearchGeoTemplate,
  "adult-compliance":         adultComplianceTemplate,
  "b2b-enterprise":           b2bEnterpriseTemplate,
  "ecommerce-dtc":            ecommerceDtcTemplate,
  "high-ticket-coaching":     highTicketCoachingTemplate,
  "saas-plg":                 saasPlgTemplate,
};

export function getTemplate(id: FunnelTemplateId): FunnelTemplate {
  return FUNNEL_TEMPLATES[id];
}

export const TEMPLATE_TO_LEAD_MAGNETS: Readonly<Record<FunnelTemplateId, readonly string[]>> = {
  "agency-white-label":       ["2026-white-label-agency-playbook", "white-label-margin-calculator", "senior-solo-seo-hiring-brief"],
  "franchise-multi-location": ["franchise-seo-multi-location-reporting-template", "multi-location-local-seo-audit-template"],
  "ai-search-geo":            ["geo-aeo-readiness-checklist", "ai-citation-tracking-template", "ai-overviews-visibility-audit-template"],
  "adult-compliance":         ["18-plus-compliance-marketing-checklist", "fan-platform-growth-playbook"],
  "b2b-enterprise":           ["b2b-saas-geo-aeo-visibility-audit", "entity-schema-implementation-guide"],
  "ecommerce-dtc":            ["dtc-funnel-conversion-checklist", "ecommerce-email-retargeting-playbook"],
  "high-ticket-coaching":     ["high-ticket-closer-call-framework", "coaching-funnel-application-template"],
  "saas-plg":                 ["plg-onboarding-checklist", "saas-free-to-paid-conversion-guide"],
};

const FIT_BANDS = ["starter", "growth", "enterprise"] as const;
const FIT_AUDIENCES = ["agency", "franchise", "creator", "adult", "enterprise"] as const;

export function parseFitBand(value: unknown): FitBand | null {
  return (FIT_BANDS as readonly string[]).includes(value as string) ? (value as FitBand) : null;
}

export function parseFitAudience(value: unknown): FitAudience | null {
  return (FIT_AUDIENCES as readonly string[]).includes(value as string) ? (value as FitAudience) : null;
}

export function recommendTemplateId(
  band?: FitBand | null,
  audience?: FitAudience | null,
): FunnelTemplateId | null {
  if (audience === "adult")     return "adult-compliance";
  if (audience === "franchise") return "franchise-multi-location";
  if (audience === "creator")   return "high-ticket-coaching";
  if (audience === "enterprise" && band === "growth") return "saas-plg";
  if (band === "enterprise")    return "b2b-enterprise";
  if (band === "starter")       return "ecommerce-dtc";
  if (band === "growth")        return "agency-white-label";
  return null;
}

export function createInitialState(opts: {
  fitQuizBand?: FitBand | null;
  fitQuizAudience?: FitAudience | null;
}): FunnelWizardState {
  const templateId = recommendTemplateId(opts.fitQuizBand, opts.fitQuizAudience);
  const template = templateId ? getTemplate(templateId) : null;

  return {
    step: 1,
    templateId,
    audience: opts.fitQuizAudience ?? null,
    goal: "",
    regionalSettings: createRegionalSettings("us"),
    audienceGoalDetails: {},
    trafficChannels: template ? [...template.defaultTrafficChannels] : [],
    stages: template
      ? template.defaultStages.map((s) => ({
          ...s,
          name: s.id,
          stageType: s.stageType ?? "custom",
          publishedStatus: "draft" as const,
          requiredStage: true,
          assetStatus: "draft" as const,
          trackingStatus: "not_set" as const,
          waitDays: 0,
          priority: "medium" as const,
        }))
      : [],
    products: [],
    capacityConfig: undefined,
    projectionInputs: { monthlyVisitors: 1_000, revenuePerLead: 2_000, projectionMonths: DEFAULT_PROJECTION_MONTHS, reinvestmentPercent: 0 },
    expenses: [],
    scenarios: defaultScenarioSnapshots(),
    experiments: [],
    packageRecommendation: { ...DEFAULT_PACKAGE_RECOMMENDATION },
    seasonalDemand: { ...DEFAULT_SEASONAL_DEMAND },
    calculationReceipt: DEFAULT_CALCULATION_RECEIPT,
    contact: null,
    fitQuizBand: opts.fitQuizBand ?? null,
    startedAt: new Date().toISOString(),
    completedAt: null,
    version: CURRENT_VERSION,
  };
}

export function defaultScenarioSnapshots(): ScenarioSnapshot[] {
  return [
    {
      id: "baseline",
      name: "Baseline",
      kind: "baseline",
      cvrMultiplier: 1,
      visitorMultiplier: 1,
      revenueMultiplier: 1,
      changedAssumptions: ["Current planner inputs"],
    },
    {
      id: "conservative",
      name: "Conservative",
      kind: "conservative",
      cvrMultiplier: 0.8,
      visitorMultiplier: 0.9,
      revenueMultiplier: 0.9,
      changedAssumptions: ["Lower conversion", "Lower traffic", "Lower revenue yield"],
    },
    {
      id: "stress",
      name: "Stress",
      kind: "custom",
      cvrMultiplier: 0.55,
      visitorMultiplier: 0.65,
      revenueMultiplier: 0.8,
      changedAssumptions: ["Major CVR drop", "Traffic under-delivery", "Lower revenue yield"],
    },
    {
      id: "optimistic",
      name: "Optimistic",
      kind: "optimistic",
      cvrMultiplier: 1.2,
      visitorMultiplier: 1.15,
      revenueMultiplier: 1.1,
      changedAssumptions: ["Higher conversion", "Higher traffic", "Higher revenue yield"],
    },
  ];
}

export function totalVisitorsFromChannels(
  trafficChannels: TrafficChannel[],
  fallback: number,
): number {
  if (trafficChannels.length === 0) return fallback;
  const sum = trafficChannels.reduce((acc, c) => acc + c.monthlyVisitors, 0);
  return sum > 0 ? sum : fallback;
}

function graphHasStages(graph?: FunnelGraph): graph is FunnelGraph {
  return !!graph && graph.nodes.some((node) => node.type === "stage");
}

function effectiveStages(stages: FunnelStageConfig[], graph?: FunnelGraph): FunnelStageConfig[] {
  if (!graphHasStages(graph)) return stages;
  const graphStages = graphToStages(graph.nodes, graph.edges);
  return graphStages.length > 0 ? graphStages : stages;
}

function graphWithAdjustedAssumptions(
  graph: FunnelGraph | undefined,
  cvrMultiplier: number,
  visitorMultiplier = 1,
): FunnelGraph | undefined {
  if (!graphHasStages(graph)) return graph;
  return {
    nodes: graph.nodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        conversionRate: node.type === "stage"
          ? Math.min(100, Math.max(0, (node.data.conversionRate ?? 10) * cvrMultiplier))
          : node.data.conversionRate,
        monthlyVisitors: node.data.isSource && node.data.monthlyVisitors != null
          ? Math.max(0, Math.round(node.data.monthlyVisitors * visitorMultiplier))
          : node.data.monthlyVisitors,
      },
    })),
    edges: graph.edges.map((edge) => ({
      ...edge,
      conversionRate: edge.conversionRate == null
        ? edge.conversionRate
        : Math.min(100, Math.max(0, edge.conversionRate * cvrMultiplier)),
      edgeCvr: edge.edgeCvr == null
        ? edge.edgeCvr
        : Math.min(100, Math.max(0, edge.edgeCvr * cvrMultiplier)),
    })),
  };
}

function graphWithStageRates(graph: FunnelGraph | undefined, stages: FunnelStageConfig[]): FunnelGraph | undefined {
  if (!graphHasStages(graph)) return graph;
  const rateByStageId = new Map(stages.map((stage) => [stage.id, stage.conversionRate]));
  return {
    ...graph,
    nodes: graph.nodes.map((node) => {
      if (node.type !== "stage") return node;
      const stageId = node.id.startsWith("stage-") ? node.id.slice(6) : node.id;
      const conversionRate = rateByStageId.get(stageId);
      if (conversionRate == null) return node;
      return {
        ...node,
        data: {
          ...node.data,
          conversionRate,
        },
      };
    }),
  };
}

function finiteNonNegativeProjectionValue(value: number): number {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

function finiteProjectionRate(value: number): number {
  return Number.isFinite(value) ? Math.min(Math.max(value, 0), 100) : 0;
}

export function computeMonthlyReinvestmentBudget(
  baselineMonthlyRevenue: number,
  reinvestmentPercent: number,
  reinvestmentCap?: number,
): number {
  const baseline = finiteNonNegativeProjectionValue(baselineMonthlyRevenue);
  const rate = finiteProjectionRate(reinvestmentPercent);
  const requestedBudget = baseline * (rate / 100);
  const finiteRequestedBudget = Number.isFinite(requestedBudget) ? requestedBudget : 0;
  const cap = reinvestmentCap == null || reinvestmentCap === Number.POSITIVE_INFINITY
    ? Number.POSITIVE_INFINITY
    : finiteNonNegativeProjectionValue(reinvestmentCap);

  return Math.min(finiteRequestedBudget, cap);
}

/** Builds the planner baseline with zero automatic monthly growth. */
function projectFlatBaseline(input: RoiInput, requestedMonths: number): RoiProjection {
  const monthlyVisitors = finiteNonNegativeProjectionValue(input.monthlyVisitors);
  const conversionRate = finiteProjectionRate(input.conversionRate);
  const revenuePerLead = finiteNonNegativeProjectionValue(input.revenuePerLead);
  const projectionMonths = Number.isFinite(requestedMonths)
    ? Math.max(MIN_PROJECTION_MONTHS, Math.min(Math.round(requestedMonths), MAX_PROJECTION_MONTHS))
    : DEFAULT_PROJECTION_MONTHS;
  const calculatedBaselineRevenue = monthlyVisitors * (conversionRate / 100) * revenuePerLead;
  const baselineRevenue = Number.isFinite(calculatedBaselineRevenue) ? calculatedBaselineRevenue : 0;
  const points = Array.from({ length: projectionMonths + 1 }, (_, month) => ({
    month,
    revenue: baselineRevenue,
  }));

  return {
    baselineRevenue,
    projectedRevenue: baselineRevenue,
    projectionMonths,
    points,
  };
}

export function computeProjection(
  stages: FunnelStageConfig[],
  inputs: ProjectionInputs,
  trafficChannels?: TrafficChannel[],
  graph?: FunnelGraph,
): RoiProjection {
  const safeInputs = {
    ...inputs,
    monthlyVisitors: finiteNonNegativeProjectionValue(inputs.monthlyVisitors),
    revenuePerLead: finiteNonNegativeProjectionValue(inputs.revenuePerLead),
  };
  const metrics = computeFunnelVolumeMetrics(stages, safeInputs, trafficChannels ?? [], graph);
  const visitors = finiteNonNegativeProjectionValue(metrics.visitors);
  const customers = finiteNonNegativeProjectionValue(metrics.customers);
  const chainedCvr = visitors > 0 ? finiteProjectionRate((customers / visitors) * 100) : 0;

  const baseProjection = projectFlatBaseline({
    monthlyVisitors: visitors,
    conversionRate: chainedCvr,
    revenuePerLead: safeInputs.revenuePerLead,
  }, inputs.projectionMonths);

  const reinvestmentPercent = finiteProjectionRate(inputs.reinvestmentPercent ?? 0);
  if (reinvestmentPercent <= 0 || visitors <= 0 || chainedCvr <= 0) {
    return baseProjection;
  }

  const revenuePerVisitor = (chainedCvr / 100) * safeInputs.revenuePerLead;
  const projectedCustomers = metrics.customers;
  const projectedLeads = metrics.leads;
  const calculatedTrafficCost = trafficChannels
    ? totalTrafficCost(trafficChannels, {
        leads: projectedLeads,
        customers: projectedCustomers,
        grossRevenue: baseProjection.baselineRevenue,
      })
    : 0;
  const trafficCost = Number.isNaN(calculatedTrafficCost) || calculatedTrafficCost < 0
    ? 0
    : calculatedTrafficCost;
  const costPerVisitor = trafficCost > 0
    ? trafficCost / visitors
    : Math.max(1, revenuePerVisitor * 0.15);
  let cumulativeExtraVisitors = 0;
  const points = baseProjection.points.map((point) => {
    if (point.month === 0) return point;
    const reinvestmentBudget = computeMonthlyReinvestmentBudget(
      point.revenue,
      reinvestmentPercent,
      inputs.reinvestmentCap,
    );
    cumulativeExtraVisitors += reinvestmentBudget / costPerVisitor;
    const projectedRevenue = point.revenue + cumulativeExtraVisitors * revenuePerVisitor;
    return {
      ...point,
      revenue: Number.isFinite(projectedRevenue) ? projectedRevenue : point.revenue,
    };
  });

  return {
    ...baseProjection,
    points,
    projectedRevenue: points[points.length - 1]?.revenue ?? baseProjection.projectedRevenue,
  };
}

export function scenarioProjection(
  stages: FunnelStageConfig[],
  inputs: ProjectionInputs,
  multiplier: 0.8 | 1.0 | 1.2,
  trafficChannels?: TrafficChannel[],
  graph?: FunnelGraph,
): RoiProjection {
  const adjusted = stages.map((s) =>
    s.isEditable
      ? { ...s, conversionRate: Math.min(100, s.conversionRate * multiplier) }
      : s,
  );
  return computeProjection(adjusted, inputs, trafficChannels, graphWithAdjustedAssumptions(graph, multiplier));
}

export function scenarioSnapshotProjection(
  stages: FunnelStageConfig[],
  inputs: ProjectionInputs,
  scenario: ScenarioSnapshot,
  trafficChannels?: TrafficChannel[],
  graph?: FunnelGraph,
): RoiProjection {
  const adjustedStages = stages.map((stage) =>
    stage.isEditable
      ? { ...stage, conversionRate: Math.min(100, Math.max(0.1, stage.conversionRate * scenario.cvrMultiplier)) }
      : stage,
  );
  const adjustedInputs = {
    ...inputs,
    monthlyVisitors: Math.max(1, Math.round(inputs.monthlyVisitors * scenario.visitorMultiplier)),
    revenuePerLead: Math.max(1, inputs.revenuePerLead * scenario.revenueMultiplier),
  };
  const adjustedChannels = trafficChannels?.map((channel) => ({
    ...channel,
    monthlyVisitors: Math.max(0, Math.round(channel.monthlyVisitors * scenario.visitorMultiplier)),
  }));
  return computeProjection(
    adjustedStages,
    adjustedInputs,
    adjustedChannels,
    graphWithAdjustedAssumptions(graph, scenario.cvrMultiplier, scenario.visitorMultiplier),
  );
}

export function computeStageVolumes(
  stages: FunnelStageConfig[],
  topOfFunnelVisitors: number,
): number[] {
  const volumes: number[] = [];
  let currentVolume = topOfFunnelVisitors;
  for (const stage of stages) {
    currentVolume = Math.round(currentVolume * (stage.conversionRate / 100));
    volumes.push(currentVolume);
  }
  return volumes;
}

function inferLeadVolume(
  stages: FunnelStageConfig[],
  stageVolumes: number[],
  customers: number,
): number {
  const leadStageIndex = stages.findIndex((stage) => {
    const haystack = `${stage.id} ${stage.name} ${stage.stageType}`.toLowerCase();
    return /\b(lead|opt|inquiry|application|booking|trial|signup|sign-up|mql|subscriber)\b/.test(haystack);
  });
  if (leadStageIndex >= 0) {
    return stageVolumes[leadStageIndex] ?? customers;
  }
  return stageVolumes[0] ?? customers;
}

export type FunnelVolumeMetrics = {
  visitors: number;
  leads: number;
  customers: number;
  stageVolumes: number[];
};

export function computeFunnelVolumeMetrics(
  stages: FunnelStageConfig[],
  inputs: ProjectionInputs,
  trafficChannels: TrafficChannel[],
  graph?: FunnelGraph,
): FunnelVolumeMetrics {
  if (graphHasStages(graph)) {
    const graphStages = effectiveStages(stages, graph);
    const visitors = computeGraphEntryVisitors(graph, trafficChannels, inputs.monthlyVisitors);
    const graphRows = computeGraphStageVolumeRows(graph, trafficChannels, inputs.monthlyVisitors);
    const stageVolumes = graphRows.map((row) => row.volume);
    const customers = stageVolumes.at(-1) ?? 0;
    const leads = inferLeadVolume(graphStages, stageVolumes, customers);

    return { visitors, leads, customers, stageVolumes };
  }

  const visitors = totalVisitorsFromChannels(trafficChannels, inputs.monthlyVisitors);
  const stageVolumes = computeStageVolumes(stages, visitors);
  const customers = computeCustomersFromStages(stages, inputs, trafficChannels);
  const leads = inferLeadVolume(stages, stageVolumes, customers);

  return { visitors, leads, customers, stageVolumes };
}

export type ChannelCostContext = {
  leads?: number;
  customers?: number;
  grossRevenue?: number;
};

export function estimateChannelCost(
  channel: TrafficChannel,
  context: ChannelCostContext = {},
): number | null {
  const t = channel.acquisitionType;
  if (t === "organic" || t === "organic_unknown") {
    return 0;
  }
  if (t === "cpc" && channel.cpc != null) {
    return channel.monthlyVisitors * channel.cpc;
  }
  if (t === "cpm" && channel.cpm != null && channel.ctr != null && channel.ctr > 0) {
    const impressions = channel.monthlyVisitors / (channel.ctr / 100);
    return (impressions / 1_000) * channel.cpm;
  }
  if (t === "cpv" && channel.costPerView != null) {
    return channel.monthlyVisitors * channel.costPerView;
  }
  if (t === "flat_fee" && channel.flatMonthlyFee != null) {
    return channel.flatMonthlyFee;
  }
  if (t === "cpa" && channel.costPerAcquisition != null && context.customers != null) {
    return context.customers * channel.costPerAcquisition;
  }
  if (t === "cost_per_lead" && channel.costPerLead != null && context.leads != null) {
    return context.leads * channel.costPerLead;
  }
  if (t === "revenue_share" && channel.revenueSharePercent != null && context.grossRevenue != null) {
    return context.grossRevenue * (channel.revenueSharePercent / 100);
  }
  if (t === "labor_time" && channel.laborHoursPerMonth != null && channel.hourlyCost != null) {
    return channel.laborHoursPerMonth * channel.hourlyCost;
  }
  return null;
}

export function totalTrafficCost(
  channels: TrafficChannel[],
  context: ChannelCostContext = {},
): number {
  return channels.reduce((sum, c) => {
    const cost = estimateChannelCost(c, context);
    return sum + (cost ?? 0);
  }, 0);
}

// --- V3 additions ---

export type FunnelPnL = {
  grossRevenue: number;
  refundAmount: number;
  merchantFees: number;
  cogsAmount: number;
  netRevenue: number;
  totalTrafficCost: number;
  grossProfit: number;
  operatingExpenses: number;
  netProfit: number;
  profitMarginPct: number;
};

export const SEOH_EXPENSE_PRESETS: ReadonlyArray<{
  id: FunnelExpenseCategory;
  label: string;
  category: FunnelExpenseCategory;
  amount: number;
  frequency: FunnelExpense["frequency"];
}> = [
  { id: "seo_content", label: "SEO / content", category: "seo_content", amount: 2500, frequency: "monthly" },
  { id: "development", label: "Development", category: "development", amount: 1800, frequency: "monthly" },
  { id: "tracking", label: "Tracking setup", category: "tracking", amount: 750, frequency: "one_time" },
  { id: "creative", label: "Creative production", category: "creative", amount: 1200, frequency: "monthly" },
  { id: "compliance_review", label: "Compliance review", category: "compliance_review", amount: 900, frequency: "monthly" },
  { id: "sales_ops", label: "Sales ops", category: "sales_ops", amount: 1500, frequency: "monthly" },
  { id: "hosting_tools", label: "Hosting / tools", category: "hosting_tools", amount: 350, frequency: "monthly" },
  { id: "contractor_labor", label: "Contractor labor", category: "contractor_labor", amount: 2000, frequency: "monthly" },
];

export function expenseMonthlyAmount(expense: FunnelExpense, month = 1): number {
  const startMonth = expense.startMonth ?? 1;
  const endMonth = expense.endMonth ?? Number.POSITIVE_INFINITY;
  if (month < startMonth || month > endMonth) return 0;
  if (expense.frequency === "one_time") return month === startMonth ? expense.amount : 0;
  if (expense.frequency === "quarterly") return expense.amount / 3;
  if (expense.frequency === "annual") return expense.amount / 12;
  return expense.amount;
}

export function computeMonthlyExpenseTotal(expenses: FunnelExpense[] = [], month = 1): number {
  return expenses.reduce((sum, expense) => sum + expenseMonthlyAmount(expense, month), 0);
}

export function createExpenseFromPreset(category: FunnelExpenseCategory, suffix = Date.now()): FunnelExpense {
  const preset = SEOH_EXPENSE_PRESETS.find((item) => item.category === category) ?? SEOH_EXPENSE_PRESETS[0]!;
  return {
    id: `expense-${preset.category}-${suffix}`,
    label: preset.label,
    category: preset.category,
    amount: preset.amount,
    frequency: preset.frequency,
    startMonth: 1,
  };
}

function computeCustomersFromStages(
  stages: FunnelStageConfig[],
  inputs: ProjectionInputs,
  trafficChannels: TrafficChannel[],
  graph?: FunnelGraph,
): number {
  if (graphHasStages(graph)) {
    const rows = computeGraphStageVolumeRows(graph, trafficChannels, inputs.monthlyVisitors);
    return rows.at(-1)?.volume ?? 0;
  }
  const chainedCvr = stages
    .filter((s) => s.isEditable)
    .reduce((acc, s) => acc * (s.conversionRate / 100), 1);
  const visitors = totalVisitorsFromChannels(trafficChannels, inputs.monthlyVisitors);
  return Math.round(visitors * chainedCvr);
}

export function computePnL(
  products: Product[],
  stages: FunnelStageConfig[],
  trafficChannels: TrafficChannel[],
  projectionInputs: ProjectionInputs,
  operatingExpenses = 0,
  regionalSettings?: RegionalSettings,
  graph?: FunnelGraph,
  expenses: FunnelExpense[] = [],
): FunnelPnL {
  const metrics = computeFunnelVolumeMetrics(stages, projectionInputs, trafficChannels, graph);
  const customers = metrics.customers;

  let grossRevenue = 0;
  let refundAmount = 0;
  let merchantFees = 0;
  let cogsAmount = 0;

  const primaryProducts = products.filter((p) => !p.isUpsell && !["order_bump", "upsell", "downsell"].includes(p.offerRole ?? "core"));
  const upsellProducts = products.filter((p) => p.isUpsell || ["order_bump", "upsell", "downsell"].includes(p.offerRole ?? "core"));

  for (const p of primaryProducts) {
    const unitGross = p.price;
    const units = Math.round(customers * ((p.productSalesSplitPct ?? 100) / 100));
    grossRevenue += unitGross * units;
    refundAmount += unitGross * (p.refundRatePercent / 100) * units;
    merchantFees += (unitGross * ((p.merchantFeePercent ?? regionalSettings?.defaultMerchantFeePercent ?? 2.9) / 100) + (p.merchantFixedFee ?? regionalSettings?.defaultMerchantFixedFee ?? 0)) * units;
    cogsAmount += (unitGross * (p.cogsPercent / 100) + fulfillmentUnitCost(p)) * units;
  }

  for (const p of upsellProducts) {
    const takeRate = offerTakeRate(p) / 100;
    const units = Math.round(customers * takeRate);
    const unitGross = p.price;
    grossRevenue += unitGross * units;
    refundAmount += unitGross * (p.refundRatePercent / 100) * units;
    merchantFees += (unitGross * ((p.merchantFeePercent ?? regionalSettings?.defaultMerchantFeePercent ?? 2.9) / 100) + (p.merchantFixedFee ?? regionalSettings?.defaultMerchantFixedFee ?? 0)) * units;
    cogsAmount += (unitGross * (p.cogsPercent / 100) + fulfillmentUnitCost(p)) * units;
  }

  // fallback: use revenuePerLead when no products defined
  if (products.length === 0) {
    grossRevenue = customers * projectionInputs.revenuePerLead;
    refundAmount = 0;
    merchantFees = 0;
    cogsAmount = 0;
  }

  const trafficCostTotal = totalTrafficCost(trafficChannels, {
    leads: metrics.leads,
    customers,
    grossRevenue,
  });

  const netRevenue = grossRevenue - refundAmount - merchantFees - cogsAmount;
  const grossProfit = netRevenue - trafficCostTotal;
  const operatingExpenseTotal = operatingExpenses + computeMonthlyExpenseTotal(expenses);
  const netProfit = grossProfit - operatingExpenseTotal;
  const profitMarginPct = grossRevenue > 0 ? (netProfit / grossRevenue) * 100 : 0;

  return {
    grossRevenue,
    refundAmount,
    merchantFees,
    cogsAmount,
    netRevenue,
    totalTrafficCost: trafficCostTotal,
    grossProfit,
    operatingExpenses: operatingExpenseTotal,
    netProfit,
    profitMarginPct,
  };
}

function offerTakeRate(product: Product): number {
  if (product.offerRole === "order_bump") return product.orderBumpTakeRatePct ?? product.upsellTakeRatePct ?? 20;
  if (product.offerRole === "downsell") return product.downsellTakeRatePct ?? 12;
  if (product.isUpsell || product.offerRole === "upsell") return product.upsellTakeRatePct ?? 20;
  return product.upsellTakeRatePct ?? 20;
}

function fulfillmentUnitCost(product: Product): number {
  if (product.fulfillmentCostType === "per_order" || product.fulfillmentCostType === "one_time") {
    return product.fulfillmentCostAmount ?? 0;
  }
  return 0;
}

export type FunnelKpis = {
  totalLeads: number;
  totalCustomers: number;
  totalTrafficCost: number;
  roas: number | null;
  roi: number | null;
  cpa: number | null;
  epc: number | null;
  cpl: number | null;
  ltv: number | null;
  cac: number | null;
  cacPaybackMonths: number | null;
  breakEvenVisitors: number | null;
  contributionMarginPct: number | null;
};

export function computeKpis(
  pnl: FunnelPnL,
  products: Product[],
  stages: FunnelStageConfig[],
  trafficChannels: TrafficChannel[],
  projectionInputs: ProjectionInputs,
  graph?: FunnelGraph,
): FunnelKpis {
  const metrics = computeFunnelVolumeMetrics(stages, projectionInputs, trafficChannels, graph);
  const visitors = metrics.visitors;
  const customers = metrics.customers;
  const totalLeads = metrics.leads;

  const tc = pnl.totalTrafficCost;
  const totalInvestment = tc + pnl.operatingExpenses;

  const roas = tc > 0 ? pnl.grossRevenue / tc : null;
  const roi = totalInvestment > 0 ? (pnl.netProfit / totalInvestment) * 100 : null;
  const cpa = tc > 0 && customers > 0 ? tc / customers : null;
  const epc = visitors > 0 ? (pnl.grossRevenue / visitors) * 100 : null;
  const cpl = tc > 0 && totalLeads > 0 ? tc / totalLeads : null;
  const cac = totalInvestment > 0 && customers > 0 ? totalInvestment / customers : null;

  let ltv: number | null = null;
  const subProduct = products.find(
    (p) => p.priceType === "monthly" || p.priceType === "yearly",
  );
  if (subProduct) {
    const months = subProduct.monthsRetained ?? 12;
    const monthlyPrice =
      subProduct.priceType === "yearly" ? subProduct.price / 12 : subProduct.price;
    ltv = monthlyPrice * months;
  }

  let cacPaybackMonths: number | null = null;
  if (subProduct && cac != null) {
    const monthlyPrice =
      subProduct.priceType === "yearly" ? subProduct.price / 12 : subProduct.price;
    const marginPct = 1 - subProduct.cogsPercent / 100 - subProduct.merchantFeePercent / 100;
    if (monthlyPrice > 0 && marginPct > 0) {
      cacPaybackMonths = cac / (monthlyPrice * marginPct);
    }
  }

  const contributionMarginPct =
    pnl.grossRevenue > 0
      ? ((pnl.grossRevenue - pnl.refundAmount - pnl.merchantFees - pnl.cogsAmount - tc) /
          pnl.grossRevenue) *
        100
      : null;

  let breakEvenVisitors: number | null = null;
  if (visitors > 0 && pnl.grossRevenue > 0) {
    const revenuePerVisitor = pnl.grossRevenue / visitors;
    const varCostPerVisitor = (pnl.refundAmount + pnl.merchantFees + pnl.cogsAmount + tc) / visitors;
    const contrib = revenuePerVisitor - varCostPerVisitor;
    if (contrib > 0 && pnl.operatingExpenses > 0) {
      breakEvenVisitors = Math.ceil(pnl.operatingExpenses / contrib);
    }
  }

  return {
    totalLeads,
    totalCustomers: customers,
    totalTrafficCost: tc,
    roas,
    roi,
    cpa,
    epc,
    cpl,
    ltv,
    cac,
    cacPaybackMonths,
    breakEvenVisitors,
    contributionMarginPct,
  };
}

export type SensitivityRow = {
  stageId: string;
  stageName: string;
  currentCvr: number;
  benchmarkCvr: number;
  delta10PctRevenueImpact: number;
  leverageScore: number;
  gap: "above" | "at" | "below";
};

export function sensitivityAnalysis(
  stages: FunnelStageConfig[],
  products: Product[],
  trafficChannels: TrafficChannel[],
  projectionInputs: ProjectionInputs,
  graph?: FunnelGraph,
): SensitivityRow[] {
  const baseVisitors = totalVisitorsFromChannels(trafficChannels, projectionInputs.monthlyVisitors);

  function revenueForStages(s: FunnelStageConfig[]): number {
    const customers = computeCustomersFromStages(
      s,
      projectionInputs,
      trafficChannels,
      graphWithStageRates(graph, s),
    );
    if (products.length === 0) return customers * projectionInputs.revenuePerLead;
    return products
      .filter((p) => !p.isUpsell)
      .reduce((sum, p) => sum + p.price * customers, 0);
  }

  const baseRevenue = revenueForStages(stages);

  const rows: SensitivityRow[] = stages
    .filter((s) => s.isEditable)
    .map((stage) => {
      const bumped = stages.map((s) =>
        s.id === stage.id
          ? { ...s, conversionRate: Math.min(100, s.conversionRate * 1.1) }
          : s,
      );
      const bumpedRevenue = revenueForStages(bumped);
      const delta10PctRevenueImpact = bumpedRevenue - baseRevenue;
      const meta = getStageTypeMeta(stage.stageType);
      const benchmarkCvr = meta.benchmark.median;
      const diff = stage.conversionRate - benchmarkCvr;
      const gap: "above" | "at" | "below" =
        diff > 1 ? "above" : diff < -1 ? "below" : "at";

      return {
        stageId: stage.id,
        stageName: stage.name,
        currentCvr: stage.conversionRate,
        benchmarkCvr,
        delta10PctRevenueImpact,
        leverageScore: 0,
        gap,
      };
    });

  const sorted = [...rows].sort(
    (a, b) => b.delta10PctRevenueImpact - a.delta10PctRevenueImpact,
  );
  const maxDelta = sorted[0]?.delta10PctRevenueImpact ?? 1;
  for (const row of rows) {
    row.leverageScore =
      maxDelta > 0
        ? Math.round((row.delta10PctRevenueImpact / maxDelta) * 100)
        : 0;
  }

  void baseVisitors;
  return rows.sort((a, b) => b.leverageScore - a.leverageScore);
}

export type MrrMonth = {
  month: number;
  newCustomers: number;
  activeSubscribers: number;
  mrr: number;
  churnedMrr: number;
  netMrr: number;
  cumulativeRevenue: number;
};

export function computeMrrProjection(
  product: Product,
  monthlyNewCustomers: number,
  months = 12,
): MrrMonth[] {
  const monthlyPrice =
    product.priceType === "yearly" ? product.price / 12 : product.price;
  const churnRate = (product.churnRatePercent ?? 5) / 100;
  const result: MrrMonth[] = [];
  let activeSubscribers = 0;
  let cumulativeRevenue = 0;

  for (let m = 1; m <= months; m++) {
    const churnedThisMonth = Math.round(activeSubscribers * churnRate);
    activeSubscribers = activeSubscribers - churnedThisMonth + monthlyNewCustomers;
    const mrr = activeSubscribers * monthlyPrice;
    const churnedMrr = churnedThisMonth * monthlyPrice;
    const netMrr = mrr - churnedMrr;
    cumulativeRevenue += mrr;
    result.push({
      month: m,
      newCustomers: monthlyNewCustomers,
      activeSubscribers,
      mrr,
      churnedMrr,
      netMrr,
      cumulativeRevenue,
    });
  }
  return result;
}

export function computeBreakEven(
  products: Product[],
  stages: FunnelStageConfig[],
  trafficChannels: TrafficChannel[],
  projectionInputs: ProjectionInputs,
  fixedCosts: number,
  graph?: FunnelGraph,
  expenses: FunnelExpense[] = [],
): { breakEvenVisitors: number; breakEvenMonths: number | null; contributionPerVisitor: number } {
  const visitors = graphHasStages(graph)
    ? computeGraphEntryVisitors(graph, trafficChannels, projectionInputs.monthlyVisitors)
    : totalVisitorsFromChannels(trafficChannels, projectionInputs.monthlyVisitors);
  if (visitors === 0) {
    return { breakEvenVisitors: 0, breakEvenMonths: null, contributionPerVisitor: 0 };
  }

  const pnl = computePnL(products, stages, trafficChannels, projectionInputs, 0, undefined, graph, expenses);
  const variableCosts = pnl.refundAmount + pnl.merchantFees + pnl.cogsAmount + pnl.totalTrafficCost;
  const contributionPerVisitor = (pnl.grossRevenue - variableCosts) / visitors;

  if (contributionPerVisitor <= 0) {
    return { breakEvenVisitors: 0, breakEvenMonths: null, contributionPerVisitor };
  }

  const breakEvenVisitors = Math.ceil(fixedCosts / contributionPerVisitor);
  const breakEvenMonths =
    visitors > 0 ? Math.ceil(breakEvenVisitors / visitors) : null;

  return { breakEvenVisitors, breakEvenMonths, contributionPerVisitor };
}

export type ScenarioComparisonRow = {
  id: string;
  name: string;
  kind: ScenarioSnapshot["kind"];
  month12Revenue: number;
  deltaVsBaseline: number;
  horizonRevenue: number;
  horizonDeltaVsBaseline: number;
  changedAssumptions: string[];
};

export function computeScenarioComparison(
  scenarios: ScenarioSnapshot[],
  stages: FunnelStageConfig[],
  inputs: ProjectionInputs,
  trafficChannels: TrafficChannel[],
  graph?: FunnelGraph,
): ScenarioComparisonRow[] {
  const effectiveScenarios = scenarios.length > 0 ? scenarios : defaultScenarioSnapshots();
  const baselineScenario = effectiveScenarios.find((scenario) => scenario.kind === "baseline") ?? effectiveScenarios[0]!;
  const month12Inputs = inputs.projectionMonths === 12
    ? inputs
    : { ...inputs, projectionMonths: 12 };
  const baselineHorizon = scenarioSnapshotProjection(stages, inputs, baselineScenario, trafficChannels, graph)
    .points.at(-1)?.revenue ?? 0;
  const baselineMonth12 = inputs.projectionMonths === 12
    ? baselineHorizon
    : scenarioSnapshotProjection(stages, month12Inputs, baselineScenario, trafficChannels, graph)
      .points.at(-1)?.revenue ?? 0;

  return effectiveScenarios.map((scenario) => {
    const horizonProjection = scenarioSnapshotProjection(stages, inputs, scenario, trafficChannels, graph);
    const horizonRevenue = horizonProjection.points.at(-1)?.revenue ?? 0;
    const month12Revenue = inputs.projectionMonths === 12
      ? horizonRevenue
      : scenarioSnapshotProjection(stages, month12Inputs, scenario, trafficChannels, graph)
        .points.at(-1)?.revenue ?? 0;
    return {
      id: scenario.id,
      name: scenario.name,
      kind: scenario.kind,
      month12Revenue,
      deltaVsBaseline: month12Revenue - baselineMonth12,
      horizonRevenue,
      horizonDeltaVsBaseline: horizonRevenue - baselineHorizon,
      changedAssumptions: scenario.changedAssumptions,
    };
  });
}
export type ReverseGoalType = "profit" | "revenue" | "leads" | "customers" | "booked_calls" | "cac" | "payback";

export type ReverseGoalResult = {
  goalType: ReverseGoalType;
  targetValue: number;
  currentValue: number;
  requiredVisitors: number;
  visitorGap: number;
  requiredBudget: number | null;
  requiredFinalCvrPct: number | null;
  feasible: boolean;
};

export function computeReverseGoal(
  goalType: ReverseGoalType,
  targetValue: number,
  products: Product[],
  stages: FunnelStageConfig[],
  trafficChannels: TrafficChannel[],
  projectionInputs: ProjectionInputs,
  operatingExpenses = 0,
  regionalSettings?: RegionalSettings,
  graph?: FunnelGraph,
  expenses: FunnelExpense[] = [],
): ReverseGoalResult {
  const visitors = graphHasStages(graph)
    ? computeGraphEntryVisitors(graph, trafficChannels, projectionInputs.monthlyVisitors)
    : totalVisitorsFromChannels(trafficChannels, projectionInputs.monthlyVisitors);
  const metrics = computeFunnelVolumeMetrics(stages, projectionInputs, trafficChannels, graph);
  const pnl = computePnL(products, stages, trafficChannels, projectionInputs, operatingExpenses, regionalSettings, graph, expenses);
  const kpis = computeKpis(pnl, products, stages, trafficChannels, projectionInputs, graph);
  const currentValueByGoal: Record<ReverseGoalType, number> = {
    profit: pnl.netProfit,
    revenue: pnl.grossRevenue,
    leads: metrics.leads,
    customers: metrics.customers,
    booked_calls: metrics.leads,
    cac: kpis.cac ?? 0,
    payback: kpis.cacPaybackMonths ?? 0,
  };
  const currentValue = currentValueByGoal[goalType] ?? 0;
  const lowerIsBetterGoal = goalType === "cac" || goalType === "payback";
  const finalCvr = visitors > 0 ? metrics.customers / visitors : 0;
  const revenuePerCustomer = metrics.customers > 0 ? pnl.grossRevenue / metrics.customers : projectionInputs.revenuePerLead;
  const profitPerCustomer = metrics.customers > 0 ? pnl.netProfit / metrics.customers : revenuePerCustomer;

  let requiredVisitors = visitors;
  if (goalType === "leads" || goalType === "booked_calls") {
    const leadRate = visitors > 0 ? metrics.leads / visitors : finalCvr;
    requiredVisitors = leadRate > 0 ? Math.ceil(targetValue / leadRate) : visitors;
  } else if (goalType === "customers") {
    requiredVisitors = finalCvr > 0 ? Math.ceil(targetValue / finalCvr) : visitors;
  } else if (goalType === "revenue") {
    const requiredCustomers = revenuePerCustomer > 0 ? targetValue / revenuePerCustomer : 0;
    requiredVisitors = finalCvr > 0 ? Math.ceil(requiredCustomers / finalCvr) : visitors;
  } else if (goalType === "profit") {
    const requiredCustomers = profitPerCustomer > 0 ? targetValue / profitPerCustomer : 0;
    requiredVisitors = finalCvr > 0 ? Math.ceil(requiredCustomers / finalCvr) : visitors;
  }

  const costPerVisitor = visitors > 0 ? pnl.totalTrafficCost / visitors : 0;
  const requiredBudget = !lowerIsBetterGoal && costPerVisitor > 0 ? requiredVisitors * costPerVisitor : null;
  const requiredFinalCvrPct = requiredVisitors > 0 && goalType === "customers" ? (targetValue / requiredVisitors) * 100 : null;
  const feasible = lowerIsBetterGoal
    ? currentValue > 0 && targetValue > 0 && currentValue <= targetValue
    : finalCvr > 0 && Number.isFinite(requiredVisitors);

  return {
    goalType,
    targetValue,
    currentValue,
    requiredVisitors: Math.max(0, requiredVisitors),
    visitorGap: lowerIsBetterGoal ? 0 : Math.max(0, requiredVisitors - visitors),
    requiredBudget,
    requiredFinalCvrPct,
    feasible,
  };
}

export type CapacityBottleneck = {
  bottleneck: "traffic" | "leads" | "sales_capacity" | "fulfillment" | "none";
  capacityCalls: number | null;
  projectedCalls: number;
  cappedCustomers: number | null;
  message: string;
};

export function computeCapacityBottleneck(state: FunnelWizardState): CapacityBottleneck {
  const metrics = computeFunnelVolumeMetrics(state.stages, state.projectionInputs, state.trafficChannels, state.graph);
  const config = state.capacityConfig;
  if (!config) {
    return { bottleneck: "none", capacityCalls: null, projectedCalls: metrics.leads, cappedCustomers: null, message: "No capacity limit set." };
  }
  const repCallCapacity = Math.floor((config.salesRepCount * config.hoursPerRepPerMonth * 60) / config.avgCallDurationMins);
  const capacityCalls = Math.min(config.maxCallsPerMonth ?? repCallCapacity, repCallCapacity);
  const closeRate = (config.closeRateByRepPct ?? 25) / 100;
  const cappedCustomers = Math.round(Math.min(metrics.leads, capacityCalls) * closeRate);
  if (metrics.leads > capacityCalls) {
    return {
      bottleneck: "sales_capacity",
      capacityCalls,
      projectedCalls: metrics.leads,
      cappedCustomers,
      message: "Sales capacity is lower than projected lead flow.",
    };
  }
  return {
    bottleneck: "none",
    capacityCalls,
    projectedCalls: metrics.leads,
    cappedCustomers,
    message: "Sales capacity can handle the projected lead flow.",
  };
}

export type ActualVsPlanRow = {
  stageId: string;
  stageName: string;
  plannedCvr: number;
  actualCvr: number | null;
  cvrDelta: number | null;
  plannedVolume: number;
  actualVolume: number | null;
  normalizedActualVolume: number | null;
  volumeDelta: number | null;
  actualPeriodStart: string | null;
  actualPeriodEnd: string | null;
  actualPeriodDays: number;
  status: "missing" | "partial_period" | "stale" | "ahead" | "on_track" | "behind";
};

export function computeActualVsPlan(
  stages: FunnelStageConfig[],
  projectionInputs: ProjectionInputs,
  trafficChannels: TrafficChannel[],
  graph?: FunnelGraph,
  now = new Date(),
): ActualVsPlanRow[] {
  const effective = effectiveStages(stages, graph);
  const metrics = computeFunnelVolumeMetrics(stages, projectionInputs, trafficChannels, graph);
  return effective.map((stage, index) => {
    const plannedVolume = metrics.stageVolumes[index] ?? 0;
    const actualCvr = stage.actualCvr ?? null;
    const actualVolume = stage.actualVolume ?? null;
    const actualPeriodDays = actualPeriodDaysForStage(stage);
    const normalizedActualVolume = actualVolume == null
      ? null
      : actualPeriodDays > 0
        ? actualVolume * (30 / actualPeriodDays)
        : actualVolume;
    const cvrDelta = actualCvr == null ? null : actualCvr - stage.conversionRate;
    const volumeDelta = normalizedActualVolume == null ? null : normalizedActualVolume - plannedVolume;
    const stale = isActualPeriodStale(stage.actualPeriodEnd, now);
    const partial = actualVolume != null && actualPeriodDays < 25;
    const status: ActualVsPlanRow["status"] =
      actualCvr == null && actualVolume == null
        ? "missing"
        : stale
          ? "stale"
          : partial
            ? "partial_period"
            : (cvrDelta ?? 0) < -1 || (volumeDelta ?? 0) < -Math.max(10, plannedVolume * 0.1)
              ? "behind"
              : (cvrDelta ?? 0) > 1 || (volumeDelta ?? 0) > Math.max(10, plannedVolume * 0.1)
                ? "ahead"
                : "on_track";
    return {
      stageId: stage.id,
      stageName: stage.name,
      plannedCvr: stage.conversionRate,
      actualCvr,
      cvrDelta,
      plannedVolume,
      actualVolume,
      normalizedActualVolume,
      volumeDelta,
      actualPeriodStart: stage.actualPeriodStart ?? null,
      actualPeriodEnd: stage.actualPeriodEnd ?? null,
      actualPeriodDays,
      status,
    };
  });
}

function actualPeriodDaysForStage(stage: FunnelStageConfig): number {
  if (stage.actualPeriodDays != null) return stage.actualPeriodDays;
  if (stage.actualPeriodStart && stage.actualPeriodEnd) {
    const start = Date.parse(stage.actualPeriodStart);
    const end = Date.parse(stage.actualPeriodEnd);
    if (Number.isFinite(start) && Number.isFinite(end) && end >= start) {
      return Math.max(1, Math.round((end - start) / 86_400_000) + 1);
    }
  }
  return 30;
}

function isActualPeriodStale(periodEnd: string | undefined, now: Date): boolean {
  if (!periodEnd) return false;
  const parsed = Date.parse(periodEnd);
  if (!Number.isFinite(parsed)) return false;
  return now.getTime() - parsed > 45 * 86_400_000;
}

export type TrackingPlanTrigger =
  | "page_view"
  | "form_submit"
  | "email_click"
  | "booking_submit"
  | "purchase"
  | "activation"
  | "custom_event";

export type TrackingPlanAttributionRole = "first_touch" | "assist" | "conversion" | "retention";

export type TrackingPlanRow = {
  stageId: string;
  stageName: string;
  stageType: string;
  eventName: string;
  outputMetric: OutputMetric;
  trigger: TrackingPlanTrigger;
  attributionRole: TrackingPlanAttributionRole;
  trackingStatus: NonNullable<FunnelStageConfig["trackingStatus"]>;
  gtmFields: string[];
  posthogFields: string[];
};

const PURCHASE_STAGE_TYPES = new Set([
  "order-form",
  "cart-page",
  "checkout",
  "free-plus-shipping",
  "tripwire-page",
  "upsell",
  "downsell",
  "product-detail-page",
]);

const BOOKING_STAGE_TYPES = new Set([
  "booking-page",
  "discovery-call",
  "application-page",
  "application",
  "proposal",
  "retainer-close",
]);

const FORM_STAGE_TYPES = new Set([
  "landing-page",
  "opt-in-page",
  "quiz-page",
  "calculator-page",
  "waitlist-page",
  "webinar-registration",
  "survey-split",
  "affiliate-signup",
]);

function trackingSlug(value: string): string {
  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 42);
  return slug || "stage";
}

function trackingTriggerForStage(stage: FunnelStageConfig): TrackingPlanTrigger {
  const stageType = stage.stageType ?? "custom";
  if (PURCHASE_STAGE_TYPES.has(stageType)) return "purchase";
  if (BOOKING_STAGE_TYPES.has(stageType)) return "booking_submit";
  if (FORM_STAGE_TYPES.has(stageType)) return "form_submit";
  if (stageType.includes("email")) return "email_click";
  if (stageType === "activation-event" || stageType.includes("onboarding")) return "activation";
  if (stageType === "blog-post" || stageType === "advertorial" || stageType === "case-study") return "page_view";
  return "custom_event";
}

function attributionRoleForStage(stage: FunnelStageConfig, index: number): TrackingPlanAttributionRole {
  const group = getStageTypeMeta(stage.stageType)?.group;
  if (index === 0 || group === "acquisition") return "first_touch";
  if (group === "conversion") return "conversion";
  if (group === "retention") return "retention";
  return "assist";
}

export function generateTrackingPlan(stages: FunnelStageConfig[]): TrackingPlanRow[] {
  return stages.map((stage, index) => {
    const meta = getStageTypeMeta(stage.stageType);
    const outputMetric = meta.outputMetric;
    const trigger = trackingTriggerForStage(stage);
    const stageType = stage.stageType ?? "custom";
    const eventName = `fp_${index + 1}_${trackingSlug(stageType)}_${trackingSlug(outputMetric)}`;
    const attributionRole = attributionRoleForStage(stage, index);
    const commerceFields = outputMetric === "customers" || outputMetric === "revenue"
      ? ["conversion_value", "currency", "product_role"]
      : [];
    const gtmFields = [
      "event",
      "stage_id",
      "stage_type",
      "funnel_step",
      "conversion_type",
      ...commerceFields,
      "utm_source",
      "utm_medium",
      "utm_campaign",
    ];
    const posthogFields = [
      "stage_id",
      "stage_type",
      "template_id",
      "locale",
      "output_metric",
      "attribution_role",
      ...commerceFields,
      "traffic_source",
    ];

    return {
      stageId: stage.id,
      stageName: stage.name,
      stageType,
      eventName,
      outputMetric,
      trigger,
      attributionRole,
      trackingStatus: stage.trackingStatus ?? "not_set",
      gtmFields,
      posthogFields,
    };
  });
}

export type ProductRevenueRow = {
  productId: string;
  productName: string;
  role: Product["offerRole"];
  units: number;
  grossRevenue: number;
  netRevenue: number;
  marginPct: number;
};

export function computeProductRevenueRows(
  products: Product[],
  stages: FunnelStageConfig[],
  trafficChannels: TrafficChannel[],
  projectionInputs: ProjectionInputs,
  regionalSettings?: RegionalSettings,
  graph?: FunnelGraph,
): ProductRevenueRow[] {
  const customers = computeCustomersFromStages(stages, projectionInputs, trafficChannels, graph);
  return products.map((product) => {
    const role = product.offerRole;
    const takeRate = role === "core" || role === "backend" || role === "continuity" ? (product.productSalesSplitPct ?? 100) : offerTakeRate(product);
    const units = Math.round(customers * (takeRate / 100));
    const grossRevenue = product.price * units;
    const fees = (product.price * ((product.merchantFeePercent ?? regionalSettings?.defaultMerchantFeePercent ?? 2.9) / 100) + (product.merchantFixedFee ?? regionalSettings?.defaultMerchantFixedFee ?? 0)) * units;
    const refunds = grossRevenue * (product.refundRatePercent / 100);
    const cogs = (product.price * (product.cogsPercent / 100) + fulfillmentUnitCost(product)) * units;
    const netRevenue = grossRevenue - fees - refunds - cogs;
    return {
      productId: product.id,
      productName: product.name,
      role,
      units,
      grossRevenue,
      netRevenue,
      marginPct: grossRevenue > 0 ? (netRevenue / grossRevenue) * 100 : 0,
    };
  });
}

export function computeTotalWaitDays(stages: FunnelStageConfig[], graph?: FunnelGraph): number {
  if (graphHasStages(graph)) {
    const nodeWait = graph.nodes.reduce((sum, node) => sum + (node.data.waitDays ?? 0), 0);
    const edgeWait = graph.edges.reduce((sum, edge) => sum + (edge.waitDays ?? 0), 0);
    return nodeWait + edgeWait;
  }
  return stages.reduce((sum, stage) => sum + (stage.waitDays ?? 0), 0);
}

export type AssetInventoryRow = {
  stageId: string;
  stageName: string;
  assetUrl: string | null;
  assetType: FunnelStageConfig["assetType"] | null;
  sourceSystem: FunnelStageConfig["assetSourceSystem"] | null;
  thumbnailUrl: string | null;
  owner: string | null;
  status: FunnelStageConfig["assetExecutionStatus"];
  trackingStatus: NonNullable<FunnelStageConfig["trackingStatus"]>;
  dueInDays: number | null;
  ctaNotes: string | null;
};

export function buildAssetInventory(stages: FunnelStageConfig[]): AssetInventoryRow[] {
  return stages.map((stage) => ({
    stageId: stage.id,
    stageName: stage.name,
    assetUrl: stage.assetUrl?.trim() || null,
    assetType: stage.assetType ?? null,
    sourceSystem: stage.assetSourceSystem ?? null,
    thumbnailUrl: stage.assetThumbnailUrl?.trim() || null,
    owner: stage.owner?.trim() || null,
    status: stage.assetExecutionStatus ?? "unassigned",
    trackingStatus: stage.trackingStatus ?? "not_set",
    dueInDays: stage.dueInDays ?? null,
    ctaNotes: stage.assetCtaNotes?.trim() || null,
  }));
}
